import type { BuildableModuleType, Cell } from "@station-keeping/sim";
import { create } from "zustand";

export type Panel = "build" | "failures" | "ships" | "station";

export type ToastTone = "info" | "good" | "warn" | "alert";

export interface Toast {
  id: number;
  text: string;
  tone: ToastTone;
}

export interface GameUiState {
  /** Panneau ouvert (barre latérale sur grand écran, tiroir sur téléphone). */
  panel: Panel | null;
  /** Module sélectionné : sa fiche remplace le panneau. */
  selection: string | null;
  /** Vaisseau sélectionné : sa fiche remplace le panneau. */
  selectedShip: string | null;
  /** Module en cours de placement. */
  buildType: BuildableModuleType | null;
  /** Case sous le curseur clavier. */
  cursor: Cell;
  /** Case survolée à la souris. */
  hover: Cell | null;
  /** Le plan de la station a le focus clavier : le curseur s'affiche. */
  canvasFocused: boolean;
  toasts: Toast[];
  /** Niveau que la station vient d'atteindre : sa fenêtre s'ouvre jusqu'à ce qu'on la ferme. */
  levelUp: number | null;
  openPanel(panel: Panel | null): void;
  togglePanel(panel: Panel): void;
  select(moduleId: string | null): void;
  selectShip(shipId: string | null): void;
  /** Ferme le panneau et la fiche ouverts (clic en dehors). */
  closeAll(): void;
  /** Repart d'une interface vierge (changement de compte, déconnexion). */
  reset(): void;
  startBuild(type: BuildableModuleType): void;
  cancelBuild(): void;
  setCursor(cell: Cell): void;
  setHover(cell: Cell | null): void;
  setCanvasFocused(focused: boolean): void;
  toast(text: string, tone?: ToastTone): void;
  dismissToast(id: number): void;
  showLevelUp(level: number): void;
  dismissLevelUp(): void;
}

let toastId = 0;
const TOAST_MS = 4000;
const MAX_TOASTS = 4;

export const useGameUi = create<GameUiState>((set, get) => ({
  panel: null,
  selection: null,
  selectedShip: null,
  buildType: null,
  cursor: { x: 0, y: 0 },
  hover: null,
  canvasFocused: false,
  toasts: [],
  levelUp: null,
  openPanel: (panel) => set({ panel, selection: null, selectedShip: null }),
  togglePanel: (panel) =>
    set((state) => ({
      panel: state.panel === panel && !state.selection && !state.selectedShip ? null : panel,
      selection: null,
      selectedShip: null,
    })),
  select: (moduleId) => set({ selection: moduleId, selectedShip: null, buildType: null }),
  selectShip: (shipId) => set({ selectedShip: shipId, selection: null, buildType: null }),
  closeAll: () => set({ panel: null, selection: null, selectedShip: null }),
  reset: () => set({ panel: null, selection: null, selectedShip: null, buildType: null, hover: null, toasts: [], levelUp: null }),
  startBuild: (type) => set({ buildType: type, selection: null }),
  cancelBuild: () => set({ buildType: null }),
  setCursor: (cell) => set({ cursor: cell }),
  setHover: (cell) => set({ hover: cell }),
  setCanvasFocused: (focused) => set({ canvasFocused: focused }),
  toast: (text, tone = "info") => {
    toastId += 1;
    const id = toastId;
    set((state) => ({ toasts: [...state.toasts, { id, text, tone }].slice(-MAX_TOASTS) }));
    setTimeout(() => get().dismissToast(id), TOAST_MS);
  },
  dismissToast: (id) => set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) })),
  // Deux niveaux d'un coup : la fenêtre montre le plus haut (ce qu'il apporte inclut le reste).
  showLevelUp: (level) => set((state) => ({ levelUp: Math.max(level, state.levelUp ?? 0) })),
  dismissLevelUp: () => set({ levelUp: null }),
}));
